import React, { useRef, useState, useEffect, Fragment, useCallback, forwardRef, useImperativeHandle } from 'react'
import PrimeTable from '@/components/primeTable'
import DictTree, { Action } from './components/dictTree'
import { Row, Col, Button, Select, Space, App, Switch, Badge, Spin, Input, Upload, Tree, Tooltip } from 'antd'
import PageHeader from '@/components/pageHeader'
import { useTranslation } from 'react-i18next'
import ExportDict from './components/exportDict'
import cls from 'classnames'
import {
  queryWhoDRALlt,
  queryMedDRALlt,
  queryVersion,
  queryDirectory,
  queryDictItems,
  getSourceItems,
  deleteDictItem,
  unDeleteDictItem,
  insertDirectory,
  knowledgeDirectoryInsert,
  knowledgeUpdateDirectory,
  knowledgeDeleteDirectory,
  updateDirectory,
  deleteDirectory,
  createDictVersion,
  prePublishVersion,
  publishVersion as publishVersionApi,
  approveDictItem,
  moveDir,
  mergeDir,
  moveItem,
  batchApprove,
  knowledgeDir,
  deleteKnowledgeItem,
  unDeleteKnowledgeItem,
  approveKnowledgeItem,
  batchApproveKnowledgeItem,
  submitKnowledgeVersion,
  prePublishKnowledgeVersion,
  publishKnowledgeVersion,
  quoteProject
} from '@/api/dictionary'
import QueryForm from './components/queryForm'
import styles from './index.module.less'
import { EditIcon, DeleteIcon, CancelIcon, PassIcon, MoveIcon, ViewIcon } from '@/components/iconButton'
import EditDictItem from './components/editDictItem'
import EditKnowledgeItem from './components/editKnowledgeItem'
import MoveDictItem from './components/moveItem'
import AddVersion from './components/addVersion'
import { drugNameTypes, PageType } from './constants'
import { useSearchParams } from 'react-router-dom'
import PopConfirm from '@/components/popConfirm'
import dayjs from 'dayjs'
import { permissionAuth, permissionStyle } from '@/utils/permission'
import useStore from '@/store'
import { download } from '@/utils'
import { findNode } from './components/dictTree/utils'
import { Resizable } from 're-resizable'
import QueryDrugForm from './components/queryDrugForm'
import { ReactComponent as Excel } from '@/assets/icons/excel.svg'
import { ReactComponent as Pic } from '@/assets/icons/img.svg'
import InstructionModal from './components/instructionModal'
import { inputExcel, dictImport } from '@/api/export'
import QueryOMOP from './components/queryOMOP'
import QueryMedRra from './components/queryMedra'
import AddVMultiFileDictModal from './components/addVMultiFileDict/modal'
import MeddraCateTreeIcon from './components/dictTree/meddraCateTreeIcon'
import QueryWHODrugB3 from './components/queryWHODrugB3'
import QueryWHODrugC3 from './components/queryWHODrugC3'
import { QuoteType } from '@/views/exportRecord/constant'
import { formaMeddratLltData } from './components/dictTree/utils'
import OmopMappingResModal from './components/omopMappingResModal'
import { ApartmentOutlined } from '@ant-design/icons'

enum EditFlag {
  DELETE = 3,
}

type DictVersion = {
  dictCode?: string,
  dictId?: string,
  dictName?: string,
  label?: string,
  operateFlag?: string,
  showRed?: boolean,
  value?: number,
  version?: string,
  status?: 1 | 2 | 3
}

const renderColumn = (key: string, record: any, pageType: PageType) => {
  const newKey = `${key}New`
  if ((record[key] === undefined || record[key] === '' || record[key] === null) && !record[newKey]) {
    return '-'
  }

  switch (pageType) {
    case PageType.View:
      return record[key]
    case PageType.Edit:
      const del = record.editFlag === EditFlag.DELETE
      return (
        <div
          style={{
            textDecoration: del ? 'line-through' : 'none',
          }}
        >
          {record[newKey] || record[key]}
        </div>
      )
    case PageType.Audit:
      const blue = record.editFlag > 0
      const oldDel = record.editFlag === EditFlag.DELETE || record[newKey]
      return (
        <div style={blue ? { color: '#3A70E2' } : {}}>
          <div
            style={{
              textDecoration: oldDel ? 'line-through' : 'none',
            }}
          >
            {record[key]}
          </div>
          {record[newKey] && <div>{record[newKey]}</div>}
        </div>
      )
  }
}

interface DictionaryProps {
  pageType?: PageType
  onQuote?: (data: any, quoteType, lltData?: any) => void
  dictionaryRef?: any
}

// 判断是否是微应用环境
const _window = window as any
const isMicro = !!_window.__MICRO_APP_ENVIRONMENT__

// 是否有ilink的引用权限
const microData = (window as any).microApp?.getData() || {
  authQuote: true,
}

const Dictionary = forwardRef(({ pageType = PageType.View, onQuote }: DictionaryProps, ref) => {
  const reloadPage = () => {
    tableRef.current.refresh()
  }
  useImperativeHandle(ref, () => ({
    reloadPage
  }));
  const { setDomainRedFlag, quoteInfo } = useStore() as any
  const type = pageType === PageType.Quote ? PageType.View : pageType
  const { t } = useTranslation()
  const { modal, message } = App.useApp()
  const [searchParams] = useSearchParams()
  const domainId = searchParams.get('domainId') as any
  const dictName = searchParams.get('dictName')
  const domainName = searchParams.get('domainName')
  const kowledgeIder = searchParams.get('kowledge')
  const dictCode = searchParams.get('dictCode')
  const dictId = searchParams.get('dictId')
  const isControlled = searchParams.get('isControlled')
  const isOMOP = searchParams.get('isOMOP')
  const isWHODrugB3 = searchParams.get('isWHODrugB3')
  const isWHODrugC3 = searchParams.get('isWHODrugC3')
  const isMedDRA = searchParams.get('isMedDRA')
  const moduleType = searchParams.get('moduleType')
  const projectId = searchParams.get('projectId')
  const searchName = searchParams.get('searchName')
  const projectNo = searchParams.get('projectNo')
  const isAE = dictName?.includes('CTCAE')
  const dictTreeRef = useRef<any>(null)
  const exportDictRef = useRef<any>(null)
  const editRef = useRef<any>(null)
  const moveRef = useRef<any>(null)
  const addVersionRef = useRef<any>(null)
  const editKnowledgeRef = useRef<any>(null)
  const instructionRef = useRef<any>(null)
  const tableRef = useRef<any>(null)
  const [versions, setVersions] = useState<DictVersion[]>([])
  const [dictVersion, setDictVersion] = useState(quoteInfo.dictId || null)
  const [operateFlag, setOperateFlag] = useState(true)
  const [loading, setLoading] = useState(false)
  const [sourceOptions, setSourceOptions] = useState([])
  const [firstLoad, setFirstLoad] = useState(false)
  const [selectDir, setSelectDir] = useState<any>(
    quoteInfo.directoryId
      ? {
        id: parseInt(quoteInfo.directoryId) || '',
        name: quoteInfo.directoryName || '',
      }
      : {}
  )
  const [switchStatus, setSwitchStatus] = useState(false)
  const [queryParams, setQueryParams] = useState({})
  const queryRef = useRef<any>(null)
  const [pageLoading, setPageLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<any>([])
  const [downExportLoading, setDownExportLoading] = useState({ downLoading: false, exportLoading: false })
  const [dictStatus, setDictStatus] = useState<1 | 2 | 3>()
  const [addVLoading, setAddVLoading] = useState(false)
  const [lltData, setLltData] = useState([])
  const [lltRowId, setLltRowId] = useState('')
  // const firstReq = useRef(false)

  // add version modal ref for multifileDict
  const addVMultiFileDictRef = useRef<any>(null);
  // 查看omop术语映射结果弹窗
  const omopMappingResRef = useRef<any>(null)

  useEffect(() => {
    setSelectedRowKeys([])
    setLltData([])
    setLltRowId('')
  }, [dictVersion, selectDir.id, queryParams])

  const konwledgeLoopViewDictname = (res) => {
    if (!kowledgeIder) {
      return res
    }
    const ultimatelyResult: any = []
    let fiar: any = []
    res.forEach((item) => {
      const containCTCAE = item?.dictName?.includes('CTCAE')
      const existCTCAEIndex = ultimatelyResult.findIndex((dict: any) => dict?.dictName?.includes('CTCAE'))
      if (containCTCAE && existCTCAEIndex < 0) {
        ultimatelyResult.push({ ...item, dictName: 'CTCAE', label: 'CTCAE' })
      } else if (containCTCAE && existCTCAEIndex >= 0) {
        ultimatelyResult[existCTCAEIndex].child = ultimatelyResult[existCTCAEIndex].child.concat(item.child)
      } else if (!containCTCAE) ultimatelyResult.push({ ...item })
    })
    const loop = (arr: any) => {
      arr.forEach((item: any) => {
        if (dictId === String(item.dictId)) fiar = [{ ...item }]
        else if (item.child) loop(item.child)
      })
    }
    loop(ultimatelyResult)
    return fiar
  }

  const getLltData = async (name: string, code: string, options?: { signal?: AbortSignal }) => {
    try {
      const requestAddr = isMedDRA ? queryMedDRALlt : queryWhoDRALlt
      const newData = isMedDRA
        ? { name, dictId: dictVersion, code, type: pageType }
        : { drugName: name, dictId: dictVersion, drugRecordNumber: code, type: pageType }
      const res = (await requestAddr(newData, options)) as any
      if (isMedDRA) {
        return formaMeddratLltData({ tree: res.map((item) => ({ ...item, children: item.directoryList })) })
      } else if (isWHODrugB3 || isWHODrugC3) {
        return res.map((item) => ({
          name: item.drugName,
          code: item.drugRecordNumber,
          children: item.directoryList,
        }))
      }
      return []
    } catch (err) {
      console.log('err', err)
      return []
    }
  }

  const fetchVersions = async () => {
    setAddVLoading(false)
    if (!dictVersion) {
      setPageLoading(true)
    }
    try {
      const res = (await queryVersion({
        type: type,
        dictName,
        domainId,
      })) as any
      const result = res.map((item: any) => ({
        label: item.showRed ? <Badge dot>{item.version}</Badge> : item.version,
        value: item.dictId,
        ...item,
      }))
      setVersions(result)
      setPageLoading(false)

      if (!dictVersion) {
        if (result.length) {
          setDictVersion(result[0].value)
          setOperateFlag(!!result[0]?.operateFlag)
        } else {
          setDictVersion(null)
        }
      } else {
        const targetVersion: any = result.find((item: any) => item.value === dictVersion)
        if (targetVersion) {
          setDictVersion(targetVersion.value)
          setOperateFlag(!!targetVersion.operateFlag)
        } else {
          setDictVersion(null)
        }
      }
    } catch (error) { }
  }

  useEffect(() => {
    if (!domainId || !dictName) return
    fetchVersions()
  }, [domainId, dictName, type])

  const queryDictTree = async (search: string) => {
    const result: any = !kowledgeIder
      ? await queryDirectory({
        dictId: dictVersion,
        parentId: domainId,
        type: type,
        searchWord: search,
      })
      : await knowledgeDir({
        dictId: dictVersion,
        parentId: '',
        type: type,
        searchWord: search,
      })
    return result
  }
  useEffect(() => {
    queryRef.current && queryRef.current.resetQuery()
    setLltData([])
    setLltRowId('')
  }, [dictVersion, type])

  const fetchSourceOptions = async () => {
    const res: any = await getSourceItems({
      dictId: dictVersion,
      type: type,
    })
    setSourceOptions(
      res
        .filter((i) => i)
        .map((item: string) => ({
          label: item,
          value: item,
        }))
    )
  }

  useEffect(() => {
    if (!dictVersion) return
    fetchSourceOptions()
  }, [dictVersion, type])

  const commonActionCode: any = !kowledgeIder
    ? {
      [PageType.Edit]: {
        addItem: 'ADD_ITEM_DICT_EDIT',
        editItem: 'EDIT_ITEM_DICT_EDIT',
        deleteItem: 'DELETE_ITEM_DICT_EDIT',
        editDir: 'EDIT_DIRECTORY_DICT_EDIT',
        moveItem: 'MOVE_ITEM_DICT_EDIT',
      },
      [PageType.Audit]: {
        addItem: 'ADD_ITEM_DICT_AUDIT',
        editItem: 'EDIT_ITEM_DICT_AUDIT',
        deleteItem: 'DELETE_ITEM_DICT_AUDIT',
        editDir: 'EDIT_DIRECTORY_DICT_AUDIT',
        moveItem: 'MOVE_ITEM_DICT_AUDIT',
      },
    }
    : {
      [PageType.Edit]: {
        addItem: 'ADD_ITEM_KNOWLEDGE_EDIT',
        editItem: 'EDIT_ITEM_KNOWLEDGE_EDIT',
        deleteItem: 'DELETE_ITEM_KNOWLEDGE_EDIT',
        editDir: 'EDIT_DIRECTORY_KNOWLEDGE_EDIT',
        moveItem: 'MOVE_ITEM_KNOWLEDGE_EDIT',
      },
      [PageType.Audit]: {
        addItem: 'ADD_ITEM_KNOWLEDGE_AUDIT',
        editItem: 'EDIT_ITEM_KNOWLEDGE_AUDIT',
        deleteItem: 'DELETE_ITEM_KNOWLEDGE_AUDIT',
        editDir: 'EDIT_DIRECTORY_KNOWLEDGE_AUDIT',
        moveItem: 'MOVE_ITEM_KNOWLEDGE_AUDIT',
      },
    }

  const fieldColumns = [
    {
      title: t('dict.code'),
      dataIndex: 'code',
      render(val: any, record: any) {
        return renderColumn('code', record, type)
      },
    },
    {
      title: t('dict.name'),
      dataIndex: 'name',
      render(val: any, record: any) {
        return renderColumn('name', record, type)
      },
    },
    {
      title: t('dict.source'),
      dataIndex: 'source',
      width: 300,
      render(val: any, record: any) {
        return renderColumn('source', record, type)
      },
    },
    {
      title: t('dict.type'),
      dataIndex: 'rootDirectoryName',
      render(val: any, record: any) {
        return renderColumn('rootDirectoryName', record, type)
      },
    },
    {
      title: t('dict.updateTime'),
      dataIndex: 'updateTime',
    },
  ]

  const fieldKnowledgeColumns = [
    {
      title: t('knowledge.txt1'), // 不良事件
      dataIndex: 'name',
      width: 300,
      render(val: any, record: any) {
        return renderColumn('name', record, type)
      },
    },
    {
      title: t('dict.code'), // 编码
      dataIndex: 'code',
      width: 300,
      render(val: any, record: any) {
        return renderColumn('code', record, type)
      },
    },
    {
      title: t('knowledge.txt9'), // 系统器官分类
      dataIndex: 'rootDirectoryName',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('rootDirectoryName', record, type)
      },
    },
    {
      title: t('knowledge.txt2'), // 定义
      dataIndex: 'definition',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('definition', record, type)
      },
    },
    {
      title: t('knowledge.txt3'), // 引申注释
      dataIndex: 'navigationalNote',
      width: 300,
      render(val: any, record: any) {
        return renderColumn('navigationalNote', record, type)
      },
    },
    {
      title: t('knowledge.txt4'), // 分级1
      dataIndex: 'grade1',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('grade1', record, type)
      },
    },
    {
      title: t('knowledge.txt5'), // 分级2
      dataIndex: 'grade2',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('grade2', record, type)
      },
    },
    {
      title: t('knowledge.txt6'), // 分级3
      dataIndex: 'grade3',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('grade3', record, type)
      },
    },
    {
      title: t('knowledge.txt7'), // 分级4
      dataIndex: 'grade4',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('grade4', record, type)
      },
    },
    {
      title: t('knowledge.txt8'), // 分级5
      dataIndex: 'grade5',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('grade5', record, type)
      },
    },
  ]

  const ypkKnowledgeColumns = [
    {
      title: t('knowledge.txt10'), // 通用名
      dataIndex: 'normalNameNew',
      width: 300,
      render(val: any, record: any) {
        return renderColumn('normalName', record, type)
      },
    },
    {
      title: t('knowledge.txt11'), // 商品名
      dataIndex: 'tradeNameNew',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('tradeName', record, type)
      },
    },
    {
      title: t('knowledge.txt12'), // 英文名
      dataIndex: 'englishName',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('englishName', record, type)
      },
    },
    {
      title: t('knowledge.txt13'), // 有效成分
      dataIndex: 'activeIngredient',
      width: 300,
      render(val: any, record: any) {
        return renderColumn('activeIngredient', record, type)
      },
    },
    {
      title: t('knowledge.txt14'), // 生产厂家
      dataIndex: 'manufacturer',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('manufacturer', record, type)
      },
    },
    {
      title: t('knowledge.txt15'), // 规格
      dataIndex: 'specs',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('specs', record, type)
      },
    },
    {
      title: t('knowledge.txt16'), // 剂型
      dataIndex: 'dosage',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('dosage', record, type)
      },
    },
    {
      title: t('knowledge.txt17'), // 国批文号
      dataIndex: 'approvalNo',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('approvalNo', record, type)
      },
    },
    {
      title: t('knowledge.txt18'), // 药品本位码
      dataIndex: 'drugStandardCode',
      width: 200,
      render(val: any, record: any) {
        return renderColumn('drugStandardCode', record, type)
      },
    },
    {
      title: t('knowledge.txt19'), // 附件
      dataIndex: 'id',
      render(val: any, record: any) {
        return (
          <Space>
            {record.instructionId && (
              <Excel
                onClick={() => {
                  instructionRef.current.open(record, 'detail')
                }}
              />
            )}
            {record.instructionUrl && (
              <Pic
                onClick={() => {
                  instructionRef.current.open(record, 'img')
                }}
              />
            )}
          </Space>
        )
      },
    },
  ]

  const refresh = (tableReload?: boolean) => {
    fetchVersions()
    setDomainRedFlag()
    fetchSourceOptions()
    dictTreeRef.current.refresh(false)
    if (tableReload) {
      tableRef.current.reload()
    } else {
      tableRef.current.refresh()
    }
  }
  const handleDelete = async (data: any) => {
    if (kowledgeIder) {
      await deleteKnowledgeItem(data)
    } else {
      await deleteDictItem(data)
      fetchSourceOptions()
    }

    message.success(t('ikg.success', { action: t('ikg.delete') }))
    refresh()
  }

  const handleUnDelete = async (data: any) => {
    if (kowledgeIder) {
      await unDeleteKnowledgeItem(data)
    } else {
      await unDeleteDictItem(data)
    }
    message.success(t('ikg.success', { action: t('dict.cancelDel') }))
    refresh()
  }

  const handleApprove = async (data: any) => {
    if (kowledgeIder) {
      await approveKnowledgeItem(data)
    } else {
      await approveDictItem(data)
    }
    message.success(t('ikg.success', { action: t('ikg.pass') }))
    refresh()
  }

  const handleApproveBatch = async () => {
    try {
      if (kowledgeIder) {
        await batchApproveKnowledgeItem({ itemIdList: selectedRowKeys.join(','), versionId: dictVersion })
      } else {
        await batchApprove({ itemIdList: selectedRowKeys.join(','), versionId: dictVersion })
      }
      message.success(t('ikg.success', { action: t('ikg.pass') }))
      setSelectedRowKeys([])
      refresh()
    } catch (error) {
      setSelectedRowKeys([])
      refresh()
    }
  }

  const handleMoveItem = async (data: any) => {
    await moveItem(data)
    refresh()
  }

  const resolveNewValue = (record: any) => {
    const newObject = {}
    Object.keys(record).forEach((key) => {
      const newV = record[`${key}New`]
      newV ? (newObject[key] = newV) : (newObject[key] = record[key])
    })
    return newObject
  }

  const handleViewOmopMappingRes = (record) => {
    omopMappingResRef.current.open(record);
  }

  const actionColumn = {
    title: t('ikg.operation'),
    dataIndex: 'id',
    fixed: 'right',
    width: 100,
    render(val: any, record: any) {
      // 2. 只有已提交的字典有能被审核
      const isShowAudit = pageType === PageType.Audit && record.editFlag > 0 && dictStatus === 2
      const isShowOmopViewBtn = record?.mappingResultVOList

      return (
        <Space size={24}>
          {isShowOmopViewBtn && (
            <ViewIcon title="查看" onClick={() => handleViewOmopMappingRes(record)} />
          )}
          {/* 知识维护不展示操作 moduleType === '2' && type === 3*/}
          {(moduleType === '2' && type === 3) ? null : record.editFlag !== EditFlag.DELETE && (
            <>
              <EditIcon
                onClick={() => {
                  if (kowledgeIder) {
                    editKnowledgeRef.current.open({
                      ...resolveNewValue(record),
                      directoryId: selectDir.id,
                      type: type,
                      dictId: dictVersion,
                    })
                    return
                  }
                  const newValue = {
                    code: record.codeNew ? record.codeNew : record.code,
                    name: record.nameNew ? record.nameNew : record.name,
                    source: record.sourceNew ? record.sourceNew : record.source,
                    cdiscSynonyms: record?.cdiscSynonymsNew ?? record?.cdiscSynonyms,
                    cdiscDefinition: record?.cdiscDefinitionNew ?? record?.cdiscDefinition,
                    nciPreferredTerm: record?.nciPreferredTermNew ?? record?.nciPreferredTerm,
                  }
                  editRef.current.open({
                    ...record,
                    ...newValue,
                    type: type,
                    dictId: dictVersion,
                  })
                }}
                disabled={!operateFlag}
                style={permissionStyle(commonActionCode[pageType].editItem)}
              />
              {!kowledgeIder && (
                <MoveIcon
                  disabled={!operateFlag}
                  onClick={() =>
                    moveRef.current.open({
                      itemId: record.id,
                      keyPath: selectDir.keyPath,
                    })
                  }
                  style={permissionStyle(commonActionCode[pageType].moveItem)}
                />
              )}

              <PopConfirm
                description={t('ikg.deleteConfirm')}
                onConfirm={() => {
                  handleDelete({ id: record.id, type: type, dictId: dictVersion })
                }}
              >
                <DeleteIcon disabled={!operateFlag} style={permissionStyle(commonActionCode[pageType].deleteItem)} />
              </PopConfirm>
            </>
          )}
          {record.editFlag === EditFlag.DELETE && (
            <PopConfirm
              description={t('ikg.unDeleteConfirm')}
              onConfirm={() => {
                handleUnDelete({ id: record.id, type: type, dictId: dictVersion })
              }}
            >
              <CancelIcon
                title={t('dict.cancelDel')}
                disabled={!operateFlag}
                style={permissionStyle(commonActionCode[pageType].deleteItem)}
              />
            </PopConfirm>
          )}
          {isShowAudit && (
            <PassIcon
              title={t('ikg.pass')}
              disabled={!operateFlag}
              style={permissionStyle(!kowledgeIder ? 'AUDIT_ITEM_DICT_AUDIT' : 'AUDIT_ITEM_KNOWLEDGE_AUDIT')}
              onClick={() => {
                handleApprove({ id: record.id, dictId: dictVersion })
              }}
            />
          )}
        </Space>
      )
    },
  }

  const controlledTerminology = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
      render(val: any, record: any) {
        return renderColumn('code', record, type)
      },
    },
    {
      title: 'CDISC Submission Value',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render(val: any, record: any) {
        return renderColumn('name', record, type)
      },
    },
    {
      title: 'CDISC Synonym(s)',
      dataIndex: 'cdiscSynonyms',
      key: 'cdiscSynonyms',
      ellipsis: true,
      render(val: any, record: any) {
        return renderColumn('cdiscSynonyms', record, type)
      },
    },
    {
      title: 'CDISC Definition',
      dataIndex: 'cdiscDefinition',
      key: 'cdiscDefinition',
      ellipsis: true,
      render(val: any, record: any) {
        return renderColumn('cdiscDefinition', record, type)
      },
    },
    {
      title: 'NCI Preferred Term',
      dataIndex: 'nciPreferredTerm',
      key: 'nciPreferredTerm',
      ellipsis: true,
      render(val: any, record: any) {
        return renderColumn('nciPreferredTerm', record, type)
      },
    },
  ]

  const OMOPcolumns = [
    {
      title: 'ID',
      dataIndex: 'conceptId', // 使用conceptId作为数据索引
      key: 'conceptId',
      ellipsis: true,
    },
    {
      title: t('dict.conceptCode'),
      dataIndex: 'conceptCode', // 使用conceptCode作为数据索引
      key: 'conceptCode',
      ellipsis: true,
    },
    {
      title: t('dict.name'),
      dataIndex: 'conceptName', // 使用conceptName作为数据索引
      key: 'conceptName',
      ellipsis: true,
    },
    {
      title: t('dict.classType'),
      dataIndex: 'conceptClassName', // 使用conceptClassName作为数据索引
      key: 'conceptClassName',
      ellipsis: true,
    },
    {
      title: t('dict.conceptType'),
      dataIndex: 'standardConcept', // 使用standardConcept作为数据索引
      key: 'standardConcept',
      ellipsis: true,
    },
    {
      title: t('dict.conceptDomain'),
      dataIndex: 'domainName', // 使用domainName作为数据索引
      key: 'domainName',
      ellipsis: true,
    },
    {
      title: t('ikg.operation'),
      dataIndex: 'action', // 使用domainName作为数据索引
      key: 'action',
      render(_, record) {
        const showViewBtn = record?.mappingResultVOList
        if (showViewBtn) {
          return (
            <ViewIcon title="查看" onClick={() => handleViewOmopMappingRes(record)} />
          )
        }
        return null
      }
    }
  ]

  const MedDRAcolumns = [
    {
      title: 'MedDRA Code',
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
    },
    {
      title: t('dict.lowLevelLang'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render(val) {
        return <div>
          <ApartmentOutlined className='ikg-icon-tooltip-triggle' />
          {` ${val}`}
        </div>
      }
    },
    {
      title: t('dict.lltCurrency'),
      dataIndex: 'lltCurrency',
      key: 'lltCurrency',
      ellipsis: true,
      render(val) {
        return <span className={cls(styles.llt_currency, val !== 'Y' && styles.error)}>{val}</span>
      }
    },
  ]

  const WHODrugC3columns = [
    {
      title: t('dict.drugName'),
      dataIndex: 'drugName', // Matches '药名'
      key: 'drugName',
      ellipsis: true,
      render(val) {
        return <div>
          <ApartmentOutlined className='ikg-icon-tooltip-triggle' />
          {` ${val}`}
        </div>
      }
    },
    {
      title: t('dict.drugNameType'),
      dataIndex: 'tradeOrIngredient', // Matches '药名'
      key: 'tradeOrIngredient',
      ellipsis: true,
      render(val) {
        // =001是成分名  ，！=001是商品名
        return `${val}` === '001' ? drugNameTypes[1].label : drugNameTypes[0].label
      }
    },
    {
      title: t('dict.drugRecordNo'),
      dataIndex: 'drugRecordNumber', // Matches '药编码'
      key: 'drugRecordNumber',
      ellipsis: true,
    },
    {
      title: 'MPID',
      dataIndex: 'medicinalProdId', // Matches '药编码'
      key: 'medicinalProdId',
      ellipsis: true,
      width: 300,
      render(val) {
        return <div style={{ overflowX: 'auto', width: '300px' }}>
          {val}
        </div>
      }
    },
    {
      title: t('dict.substanceName'),
      dataIndex: 'substanceName', // Matches '活性成分'
      key: 'substanceName',
      ellipsis: true,
      render(val) {
        return <div style={{ overflowX: 'auto', maxWidth: '300px' }}>
          {val}
        </div>
      }
    },
    {
      title: t('dict.country'),
      dataIndex: 'countryName', // Matches '销售国家'
      key: 'countryName',
      ellipsis: true,
      width: 300,
      render(val) {
        return <div style={{ overflowX: 'auto', maxWidth: '300px' }}>
          {val}
        </div>
      }
    },
    {
      title: t('dict.dosageForm'),
      dataIndex: 'drugType', // Matches '剂型'
      key: 'drugType',
      ellipsis: true,
    },
    {
      title: t('dict.mah'),
      dataIndex: 'mah', // Matches '剂型'
      key: 'mah',
      ellipsis: true,
      render(val) {
        return <div style={{ overflowX: 'auto', maxWidth: '300px' }}>
          {val}
        </div>
      }
    },
    {
      title: t('dict.preferredName'),
      dataIndex: 'preferredName', // 通用名
      key: 'preferredName',
      ellipsis: true,
    }
  ]

  const WHODrugB3columns = [
    {
      title: t('dict.drugName'),
      dataIndex: 'drugName', // Matches '药名'
      key: 'drugName',
      ellipsis: true,
      render(val) {
        return <div>
          <ApartmentOutlined className='ikg-icon-tooltip-triggle' />
          {` ${val}`}
        </div>
      }
    },
    {
      title: t('dict.drugNameType'),
      dataIndex: 'tradeOrIngredient', // Matches '药名'
      key: 'tradeOrIngredient',
      ellipsis: true,
      render(val) {
        // 1=001是成分名  ，！=001是商品名
        return `${val}` === '001' ? drugNameTypes[1].label : drugNameTypes[0].label
      }
    },
    {
      title: t('dict.drugRecordNo'),
      dataIndex: 'drugRecordNumber', // Matches '药编码'
      key: 'drugRecordNumber',
      ellipsis: true,
    },
    {
      title: t('dict.substanceName'),
      dataIndex: 'substanceName', // Matches '活性成分'
      key: 'substanceName',
      ellipsis: true,
      render(val) {
        return <div style={{ overflowX: 'auto', maxWidth: '300px' }}>
          {val}
        </div>
      }
    },
    {
      title: t('dict.country'),
      dataIndex: 'countryName', // Matches '销售国家'
      key: 'countryName',
      ellipsis: true,
    },
    {
      title: t('dict.preferredName'),
      dataIndex: 'preferredName', // 通用名
      key: 'preferredName',
      ellipsis: true,
    }
  ]

  const columns = [
    ...(isControlled === '1'
      ? controlledTerminology
      : isOMOP === '1'
        ? OMOPcolumns
        : isWHODrugC3
          ? WHODrugC3columns
          : isWHODrugB3
            ? WHODrugB3columns
            : isMedDRA
              ? MedDRAcolumns
              : fieldColumns),
    ...(pageType === PageType.Edit || pageType === PageType.Audit ? [actionColumn] : []),
  ]

  const knowledgeCTCAEColumns = {
    [PageType.View]: [...fieldKnowledgeColumns],
    [PageType.Edit]: [...fieldKnowledgeColumns, actionColumn],
    [PageType.Audit]: [...fieldKnowledgeColumns, actionColumn],
    [PageType.Quote]: [...fieldKnowledgeColumns],
  }[pageType] as any

  const knowledgeYPKColumns = {
    [PageType.View]: [...ypkKnowledgeColumns],
    [PageType.Edit]: [...ypkKnowledgeColumns, actionColumn],
    [PageType.Audit]: [...ypkKnowledgeColumns, actionColumn],
    [PageType.Quote]: [...ypkKnowledgeColumns],
  }[pageType] as any

  const handleTreeEdit = async (values: any, action: Action | null) => {
    let res: any = null
    switch (action) {
      case Action.ADD_SIBLING:
      case Action.ADD_CHILDREN:
        res = !kowledgeIder
          ? await insertDirectory({
            ...values,
            dictId: dictVersion,
          })
          : await knowledgeDirectoryInsert({
            ...values,
            dictId: dictVersion,
          })
        break
      case Action.EDIT:
        res = !kowledgeIder
          ? await updateDirectory({
            ...values,
          })
          : await knowledgeUpdateDirectory({ ...values })
        break
      case Action.DELETE:
        res = !kowledgeIder
          ? await deleteDirectory({
            directoryId: values.id,
          })
          : await knowledgeDeleteDirectory({
            directoryId: values.id,
          })
        break
      case Action.MOVE:
        res = await moveDir({
          ...values,
        })
        break
      case Action.MERGE:
        res = await mergeDir({
          ...values,
        })
        break
    }
    dictTreeRef.current.refresh(false)
    return res
  }

  const toLeadFile = async (data: any) => {
    if (!/(xlsx|xls)$/i.test(data.file.name)) {
      message.success(t('knowledge.formatFileError'))
      return
    }
    setDownExportLoading({ ...downExportLoading, exportLoading: true })
    try {
      const params = new FormData()
      params.append('file', data.file)
      params.append('domainId', domainId)
      params.append('dictVersionId', dictVersion)
      params.append('type', String(type))
      await dictImport(params)
      await tableRef.current.reload()
      await dictTreeRef.current.refresh(false)
      message.success(t('ikg.success', { action: t('ikg.import') }))
      setDownExportLoading({ ...downExportLoading, exportLoading: false })
    } finally {
      setDownExportLoading({ ...downExportLoading, exportLoading: false })
    }
  }

  const toDownloadFile = async () => {
    setDownExportLoading({ ...downExportLoading, downLoading: true })
    try {
      const res: any = await inputExcel({})
      download(res, 'application/vnd.ms-excel', `${t('ikg.downTemplate') + '-' + new Date().getTime()}.xlsx`)
      message.success(t('ikg.success', { action: t('ikg.downTemplate') }))
      setDownExportLoading({ ...downExportLoading, downLoading: false })
    } catch (error) {
      setDownExportLoading({ ...downExportLoading, downLoading: false })
    }
  }

  const submitVersion = () => {
    modal.confirm({
      title: t('dict.submitVersion'),
      transitionName: 'custom-slide-up',
      centered: true,
      content: t('dict.submitVersionTxt'),
      icon: null,
      onOk: async () => {
        if (kowledgeIder) {
          await submitKnowledgeVersion({
            id: dictVersion,
          })
        } else {
          await createDictVersion({
            id: dictVersion,
          })
        }
        message.success(t('ikg.success', { action: t('dict.submitVersion') }))
        refresh()
      },
    })
  }

  const publishVersion = async () => {
    const flag = kowledgeIder
      ? await prePublishKnowledgeVersion({ dictId: dictVersion })
      : await prePublishVersion({
        dictId: dictVersion,
      })
    const modalIns = modal.confirm({
      title: t('dict.publishVersion'),
      transitionName: 'custom-slide-up',
      centered: true,
      content: flag ? t('dict.publishText') : t('dict.publishText2'),
      icon: null,
      onOk: async () => {
        try {
          if (kowledgeIder) {
            await publishKnowledgeVersion({
              dictId: dictVersion,
            })
          } else {
            await publishVersionApi({
              dictId: dictVersion,
            })
          }
          message.success(t('ikg.success', { action: t('dict.publishVersion') }))
          modalIns.destroy()
          refresh()
        } catch (error) {
          modalIns.destroy()
          refresh()
        }
      },
    })
  }


  const getQuoteData = (quoteType: QuoteType) => {
    if (quoteType === QuoteType.Dict) {
      return { dictId: dictVersion, directoryId: selectDir.id }
    }

    if (quoteType === QuoteType.Dir) {
      return {
        dictId: dictVersion,
        directoryId: selectDir.id,
        projectSignboard: ''
      }
    }

    if (quoteType === QuoteType.Item) {
      let itemInfoList = []
      const tableData = tableRef?.current?.getTableData()?.data || []
      if (isMedDRA) {
        itemInfoList = tableData.filter(({ id }) => selectedRowKeys.includes(id)).map(({ id, code, name }) => ({ id, code, name }))
      } else if (isOMOP) {
        itemInfoList = tableData.filter(({ id }) => selectedRowKeys.includes(id)).map(({ id, conceptCode, conceptName }) => ({ id, code: conceptCode, name: conceptName }))
      } else if (isWHODrugB3 || isWHODrugC3) {
        itemInfoList = tableData.filter(({ id }) => selectedRowKeys.includes(id)).map(({ id, drugRecordNumber, drugName }) => ({ id, code: drugRecordNumber, name: drugName }))
      } else {
        itemInfoList = tableData.filter(({ id }) => selectedRowKeys.includes(id)).map(({ id, code, name }) => ({ id, code, name }))
      }
      return {
        dictId: dictVersion,
        directoryId: selectDir.id,
        projectSignboard: '',
        itemInfoList
      }
    }

    return {}
  }

  const handleQuote = async (quoteType: QuoteType) => {
    try {
      const data = getQuoteData(quoteType);
      if (microData.product === 'ipvs') {
        let WHODruglanguage = 'zh';
        if (isWHODrugB3 || isWHODrugC3) {
          if ((dictName || '').toLowerCase().indexOf('global') > -1) {
            WHODruglanguage = 'en'
          }
        }
        if (selectedRowKeys.length > 1) {
          return message.warning('只能选择一个词条进行引用')
        }
        const targetItem = tableRef?.current?.getTableData()?.data.find(({ id }) => selectedRowKeys.includes(id))
        const requestAddr = isMedDRA ? queryMedDRALlt : queryWhoDRALlt;
        const versionNumber = versions.find(it => it.value === dictVersion)?.version;
        const newData = isMedDRA
          ? { name: targetItem.name, dictId: dictVersion, code: targetItem.code, type: pageType }
          : { drugName: targetItem.drugName, dictId: dictVersion, drugRecordNumber: targetItem.drugRecordNumber, type: pageType }
        const itemInfoList = isMedDRA
          ? [{ code: newData.code, name: newData.name, id: targetItem.id }]
          : [{ code: newData.drugRecordNumber, name: newData.drugName, id: targetItem.id }];
        const lltData = (await requestAddr(newData)) as any
        typeof onQuote === 'function' && onQuote({
          ...data,
          versionNumber,
          targetItem,
          directoryId: selectDir.id,
          WHODruglanguage,
          itemInfoList
        },
          quoteType,
          lltData,
        )
        return
      }

      typeof onQuote === 'function' && onQuote(data, quoteType)
    } catch (err) {
      throw err
    }
  }

  // 引用字典页面, 并且是在微应用环境中
  const isShowQuoteButton = pageType === PageType.Quote && isMicro

  const renderQuoteButton = useCallback(() => {
    // ipvs引用
    if (microData.product === 'ipvs') {
      return (
        <Space>
          <Button
            type="primary"
            ghost
            onClick={() => {
              if (isMicro) {
                _window.microApp?.forceDispatch({
                  type: 'back',
                })
              }
            }}
          >{t('ikg.back')}</Button>
          <Button type="primary" onClick={() => handleQuote(QuoteType.Item)} disabled={!selectDir.id || !selectedRowKeys.length}>{t('dict.quoteItem')}</Button>
        </Space>
      )
    }
    // 目前只有ilink引用，可以直接放开
    const isShowQuoteCurCatBtn = true
    const disabledQuoteCurCatBtn = !selectDir.id
    // 目前只有ilink引用，可以直接放开
    const isShowQuoteSelectedItemBtn = true
    const disabledQuoteSelectedItemBtn = !selectDir.id || !selectedRowKeys.length
    const disabledQuoteBtn = !selectDir.id

    return (
      <Space>
        {isShowQuoteSelectedItemBtn && <Button type="primary" onClick={() => handleQuote(QuoteType.Item)} disabled={disabledQuoteSelectedItemBtn}>{t('dict.quoteItem')}</Button>}
        {isShowQuoteCurCatBtn && <Button type="primary" onClick={() => handleQuote(QuoteType.Dir)} disabled={disabledQuoteCurCatBtn}>{t('dict.quoteDir')}</Button>}
        <Button type="primary" onClick={() => handleQuote(QuoteType.Dict)} disabled={disabledQuoteBtn}>
          {t('dict.quoteDict')}
        </Button>
      </Space>
    )
  }, [selectDir, selectedRowKeys])

  /**
 * 
  iPVS引用时，只做单词条的引用
  iLINK引用时，可单选、多选词条、字典引用
*/
  const handleSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  }

  const getRowSelectionParam = () => {
    // 如果是作为微应用内嵌，可以开启引用字典功能
    if (isShowQuoteButton) {
      const isRadio = (isMedDRA || isWHODrugB3 || isWHODrugC3) && microData.product === 'ipvs';
      return {
        type: isRadio ? 'radio' : 'checkbox',
        selectedRowKeys,
        onChange: handleSelectChange
      }
    }

    // 第一步，页面层级，权限层级，可以进行审核
    if (PageType.Audit === pageType && permissionAuth(!kowledgeIder ? 'AUDIT_ITEM_DICT_AUDIT' : 'AUDIT_ITEM_KNOWLEDGE_AUDIT')) {

      // 字典类型，字典状态，可以进行审核
      // 1. MedDRA、WHODRUG、OMOP字典在字典审核页面展示checkbox
      // 2. 只有已提交的字典有checkbox
      if (isMedDRA || isWHODrugB3 || isWHODrugC3 || isOMOP) return undefined
      // 1 未提交 2 已提交 3 已发布
      if (dictStatus !== 2) return undefined

      return {
        selectedRowKeys,
        onSelect: (record: any, selected: any) => {
          const newKeys = selected
            ? [...selectedRowKeys, record.id]
            : selectedRowKeys.filter((item: any) => {
              return item !== record.id
            })
          if (newKeys.length > 100) {
            return message.warning(t('dict.batchAuditMax'))
          }
          setSelectedRowKeys(newKeys)
        },
        onSelectAll: (selected: any, selectedRows: any, changeRows: any) => {
          let newKeys: any = []
          if (selected) {
            newKeys = [...selectedRowKeys]
            changeRows.forEach((item: any) => {
              if (!newKeys.includes(item.id)) {
                newKeys.push(item.id)
              }
            })
          } else {
            const changeRowsIds = changeRows.map((i: any) => i.id)
            newKeys = selectedRowKeys.filter((item: any) => {
              return !changeRowsIds.includes(item)
            })
          }
          if (newKeys.length > 100) {
            return message.warning(t('dict.batchAuditMax'))
          }
          setSelectedRowKeys(newKeys)
        },
        getCheckboxProps: (record: any) => ({
          disabled: record.editFlag === 0, // disable checkbox when editFlag is 0
        }),
      }
    }

    return undefined
  }

  // 如果不是做为内嵌的子应用
  const showAuditPassBtn = selectedRowKeys.length > 0 && !isMicro

  // const isShowAddVersionButton = !isMedDRA && !isWHODrugB3 && !isWHODrugC3;
  const isShowAddVersionButton = true;

  const openAddVersionModal = () => {
    const isMultiFileDict = isMedDRA || isWHODrugB3 || isWHODrugC3 || isOMOP;

    if (isMultiFileDict) {
      addVMultiFileDictRef.current.open();
    } else {
      addVersionRef.current.open({
        domainId,
        dictId: dictVersion,
        dictName,
        popFromType: 2,
        moduleType,
      })
    }
  }

  const handleAddVersionStart = () => {
    setAddVLoading(true)
  }

  const isRenderCate = isMedDRA || isWHODrugB3 || isWHODrugC3

  const isNeedHideCount = (isMedDRA || isWHODrugB3 || isWHODrugC3)
    && microData.product === 'ipvs'

  /**
   * 展示导出按钮
   * 是view页面
   * &
   * 不是这几种字典类型：omop，meddra，whodrugb3，whodrugc3
   */
  const isShowExportButton = pageType === PageType.View && !kowledgeIder && !isOMOP && !isMedDRA && !isWHODrugB3 && !isWHODrugC3

  useEffect(() => {
    setDictStatus(dictVersion ? versions.find(v => v?.dictId === dictVersion)?.status : undefined)
  }, [dictVersion])

  return (
    <Spin spinning={pageLoading}>
      <PageHeader title={dictName}>
        <div className={styles.headMiddle}>
          <Select
            style={{ width: 180, margin: dictName ? '0 16px' : 0 }}
            options={versions}
            value={dictVersion}
            onChange={(value: any) => {
              setDictVersion(value)
              const targetVersion: any = versions.find((item: any) => item.value === value)
              setOperateFlag(!!targetVersion.operateFlag)
            }}
            placeholder={t('ikg.select', { name: t('ikg.version') })}
          />
          {(moduleType === '2' && type === 3) ? null : pageType === PageType.Audit && (
            <Space size={16}>
              <span>{t('dict.filterSwitch')}</span>
              <Switch size="small" onChange={setSwitchStatus} />
            </Space>
          )}
        </div>
        {isShowExportButton && (
          // 知识库暂时不需要导出
          <Button
            type="primary"
            onClick={() => {
              const today = dayjs().format('YYYY-MM-DD')
              const versionLabel = (versions.find((v: any) => v.value === dictVersion) as any)?.label
              exportDictRef.current.open({
                id: dictVersion,
                excelName: `${domainName ? domainName + '_' : ''}${dictName}_${versionLabel}_${today}`,
              })
            }}
            ghost
            style={permissionStyle('EXPORT_DICT')}
          >
            {t('ikg.export')}
          </Button>
        )}
        {pageType === PageType.Edit && (
          <>
            {!kowledgeIder ? (
              <>
                <Button
                  type="primary"
                  onClick={toDownloadFile}
                  disabled={!operateFlag}
                  loading={downExportLoading.downLoading}
                  style={permissionStyle('IMPORT_DICT_DICT_EDIT')}
                >
                  {t('ikg.downTemplate')}
                </Button>
                &emsp;
                <Upload accept=".xlsx,.xls" name="file" showUploadList={false} customRequest={toLeadFile}>
                  <Button
                    type="primary"
                    disabled={!operateFlag}
                    loading={downExportLoading.exportLoading}
                    style={permissionStyle('IMPORT_DICT_DICT_EDIT')}
                  >
                    {t('ikg.import')}
                  </Button>
                </Upload>
                &emsp;
              </>
            ) : null}
            <Button
              type="primary"
              onClick={submitVersion}
              disabled={!operateFlag}
              style={permissionStyle(!kowledgeIder ? 'SUBMIT_VERSION_DICT_EDIT' : 'SUBMIT_VERSION_KNOWLEDGE_EDIT')}
            >
              {t('dict.submitVersion')}
            </Button>
          </>
        )}
        {pageType === PageType.Audit && (
          <Space size={16}>
            {isShowAddVersionButton && (
              <Button
                loading={addVLoading}
                type="primary"
                onClick={openAddVersionModal}
                ghost
                style={permissionStyle(!kowledgeIder ? 'CREATE_VERSION_DICT_AUDIT' : 'CREATE_VERSION_KNOWLEDGE_AUDIT')}
              >
                {t('dict.addVersion')}
              </Button>
            )}

            {(moduleType === '2' && type === 3) ? null : <Button
              type="primary"
              onClick={publishVersion}
              style={permissionStyle(!kowledgeIder ? 'PUBLISH_VERSION_DICT_AUDIT' : 'PUBLISH_VERSION_KNOWLEDGE_AUDIT')}
              disabled={!operateFlag}
            >
              {t('dict.publishVersion')}
            </Button>}
          </Space>
        )}
        {isShowQuoteButton && renderQuoteButton()}
      </PageHeader>
      <Row wrap={false} gutter={24}>
        <Col>
          <Resizable
            enable={{ right: true }}
            minWidth={320}
            onResize={(event: MouseEvent | TouchEvent, dir: String, elementRef: HTMLElement) => {
              // console.log('onResize', event, elementRef.children)
              // 直接操作dom设置样式，性能更优，vm频繁渲染效率很低
              document.getElementById('treeContainerDrag')!.style.width = elementRef.offsetWidth + 'px'
            }}
          >
            <div className={styles.treeContainer} id="treeContainerDrag">
              {dictVersion && (
                <DictTree
                  contextMenu={
                    [PageType.Edit, PageType.Audit].includes(pageType) &&
                    operateFlag &&
                    (!kowledgeIder ? permissionAuth(commonActionCode[pageType].editDir) : true)
                  }
                  onClick={(n: any) => {
                    queryRef.current && queryRef.current.submitQuery()
                    setSelectDir(n)
                  }}
                  onEdit={handleTreeEdit}
                  showRed={pageType === PageType.Audit}
                  selectedKeys={[selectDir.id]}
                  queryDictTree={queryDictTree}
                  isNeedHideCount={!!isNeedHideCount}
                  onLoad={(res: any[]) => {
                    if (res && res.length > 0) {
                      if (selectDir.id) {
                        const node = findNode(res, selectDir.id)
                        if (node) {
                          setSelectDir(node)
                          // setFirstLoad(true)
                          return
                        }
                      }
                      setSelectDir(res[0])
                      // setFirstLoad(true)
                    }
                  }}
                  ref={dictTreeRef}
                  key={dictVersion}
                />
              )}
            </div>
          </Resizable>
        </Col>
        <Col style={{ maxWidth: 'calc(100% - 350px)', width: isControlled ? '100%' : '' }}>
          {!kowledgeIder && (
            <Fragment>
              {isControlled ? (
                <Input.Search
                  onSearch={(val: string) => {
                    setQueryParams({ searchCdisc: val })
                  }}
                  allowClear
                  placeholder={t('ikg.search')}
                />
              ) : isOMOP ? (
                <QueryOMOP onQuery={setQueryParams} ref={queryRef} />
              ) : isMedDRA ? (
                <QueryMedRra
                  onQuery={(val) => {
                    if (JSON.stringify(val) === JSON.stringify(queryParams)) {
                      tableRef.current.reload()
                    } else {
                      setQueryParams(val)
                    }
                  }}
                  loading={loading}
                  initialValue={{ ...(isMedDRA ? { name: searchName } : {}) }}
                  ref={queryRef}
                />
              ) : isWHODrugC3 ? (
                <QueryWHODrugC3
                  onQuery={(val) => {
                    if (JSON.stringify(val) === JSON.stringify(queryParams)) {
                      tableRef.current.reload()
                    } else {
                      setQueryParams(val)
                    }
                  }}
                  loading={loading}
                  initialValue={{ ...((isWHODrugB3 || isWHODrugC3) ? { name: searchName } : {}) }}
                  ref={queryRef}
                />
              ) : isWHODrugB3 ? (
                <QueryWHODrugB3
                  onQuery={(val) => {

                    setQueryParams(val)
                  }}
                  loading={loading}
                  ref={queryRef} />
              ) : (
                <QueryForm onQuery={setQueryParams} sourceOptions={sourceOptions} ref={queryRef} />
              )}
            </Fragment>
          )}
          {kowledgeIder && !isAE && <QueryDrugForm onQuery={setQueryParams} ref={queryRef} />}
          <div className={styles.tableTitle}>
            <span className={styles.text}>
              <span>{selectDir.name}</span>
              {!isNeedHideCount && (<span>（{selectDir.count}）</span>)}
            </span>
            <Space size={16}>
              {kowledgeIder && isAE && (
                <Input.Search
                  onSearch={(val: string) => {
                    setQueryParams({ ctcaeSearch: val })
                  }}
                  allowClear
                  placeholder={t('ikg.search')}
                />
              )}
              {showAuditPassBtn && (
                <>
                  <span className={styles.selectCount}>{t('ikg.selectCount', { count: selectedRowKeys.length })}</span>
                  <Button type="primary" ghost onClick={handleApproveBatch}>
                    {t('dict.auditPass')}
                  </Button>
                </>
              )}
              {(moduleType === '2' && type === 3) ? null : [PageType.Edit, PageType.Audit].includes(pageType) && (
                <Button
                  type="primary"
                  ghost
                  onClick={() => {
                    if (!kowledgeIder) {
                      editRef.current.open({
                        directoryId: selectDir.id,
                        type: type,
                      })
                    } else {
                      editKnowledgeRef.current.open({
                        directoryId: selectDir.id,
                        type: type,
                      })
                    }
                  }}
                  disabled={!selectDir.id || !operateFlag}
                  style={permissionStyle(commonActionCode[pageType].addItem)}
                >
                  {t('dict.addWord')}
                </Button>
              )}
            </Space>
          </div>
          <PrimeTable
            columns={!kowledgeIder ? columns : isAE ? knowledgeCTCAEColumns : knowledgeYPKColumns}
            request={queryDictItems}
            setLoading={setLoading}
            queryParams={{
              dictId: dictVersion,
              directoryId: selectDir.id,
              type: type,
              needToAgree: switchStatus ? 1 : null,
              ...(isMedDRA ? { name: searchName } : {}),
              ...queryParams,
              // 后端接口需要对这个转码
              drugName: queryParams?.drugName ? encodeURI(queryParams.drugName) : null,
            }}
            // firstReq={firstReq}
            allowFetch={!!dictVersion && !!selectDir.id}
            firstLoad={firstLoad}
            setFirstLoad={setFirstLoad}
            ref={tableRef}
            popoverTree={{
              isMedDRA: true,
              isRenderCate: !!isRenderCate,
              getLltData: async (name, code, options) => {
                // 获取树形数据的逻辑
                const data = await getLltData(name, code, options);
                return data;
              }
            }}
            rowSelection={getRowSelectionParam()}
          />
        </Col>
      </Row>
      <ExportDict ref={exportDictRef} />
      <EditDictItem
        ref={editRef}
        sourceOptions={sourceOptions}
        onSuccess={(flag: boolean) => {
          refresh(flag)
        }}
      />
      <EditKnowledgeItem
        ref={editKnowledgeRef}
        onSuccess={(flag: boolean) => {
          refresh(flag)
        }}
        isAE={isAE}
      />
      <MoveDictItem ref={moveRef} onSubmit={handleMoveItem} queryDictTree={queryDictTree} disabledKey={selectDir.key} />
      <AddVersion
        ref={addVersionRef}
        onStart={handleAddVersionStart}
        onSuccess={fetchVersions}
        operationType={2}
        type={kowledgeIder ? 'knowledge' : 'dict'}
      />
      <InstructionModal ref={instructionRef} />
      {/* add version for Multi Directory Dict */}
      <AddVMultiFileDictModal ref={addVMultiFileDictRef} onSuccess={fetchVersions} onStart={handleAddVersionStart} />
      <OmopMappingResModal ref={omopMappingResRef} />
    </Spin>
  )
})

const DictionaryBase = ({ pageType, onQuote, dictionaryRef }: DictionaryProps) => {
  const [searchParams] = useSearchParams()
  const domainId = searchParams.get('domainId')
  const dictName = searchParams.get('dictName')
  return <Dictionary ref={dictionaryRef} pageType={pageType} key={`${pageType}_${domainId}_${dictName}`} onQuote={onQuote} />
}

export default DictionaryBase

export const DictionaryView = () => <DictionaryBase pageType={PageType.View} />
export const DictionaryEdit = () => <DictionaryBase pageType={PageType.Edit} />
export const DictionaryAudit = () => <DictionaryBase pageType={PageType.Audit} />
